import type { UsageMetadata } from '@avisoma/shared/dist/ProcessingData';
import { estimateTextEmbedding3LargeUsd } from './openAiTextEmbedding3Estimate';

/** Gemini models the cost view can price. */
export type GeminiPricedModel = 'gemini-3-flash-preview' | 'gemini-3.5-flash';

type GeminiUsdPerMillion = {
  inputTextImageVideo: number;
  contextCaching: number;
  /** Billed at output rate, including thinking per Google pricing table */
  outputIncludingThinking: number;
};

/**
 * List prices per model (Standard tier, paid), per 1M tokens in USD.
 * @see https://ai.google.dev/gemini-api/docs/pricing
 */
export const GEMINI_STANDARD_USD_PER_MILLION: Record<GeminiPricedModel, GeminiUsdPerMillion> = {
  'gemini-3-flash-preview': { inputTextImageVideo: 0.5, contextCaching: 0.05, outputIncludingThinking: 3.0 },
  'gemini-3.5-flash': { inputTextImageVideo: 1.5, contextCaching: 0.15, outputIncludingThinking: 9.0 },
};

/** Gemini pipeline stages whose token usage the cost view prices. */
export type GeminiUsageStage = 'segmentation' | 'pdfParser' | 'dataExtraction';

/**
 * The model each stage is priced at. This is an approximation of the stage's default model,
 * since the usage rollups do not record the model per call. For dataExtraction the
 * `gemini-3.5-flash` price applies to the remainder only: NIS Statement and Part Movement
 * extraction run `gemini-3-flash-preview` and are priced at that rate via the category bucket
 * (`dataExtractionFlashPreview`, DR-4643) where it is recorded. Still approximate: model
 * fallbacks, Part Movement's preExtract call (flash-lite), the PDF parser's ECM trend call
 * (`gemini-3.5-flash`), a fresh PDF→HTML conversion's `gemini-3.5-flash` tokens that the
 * extractor folds into a NIS / Part Movement page's usage (priced with the bucket), the
 * `gemini-3.5-flash` overlay calls (wash, thrust conversion) that run on such a page (also
 * priced with the bucket), and documents not yet re-summed since DR-4643.
 */
export const GEMINI_STAGE_DEFAULT_MODEL: Record<GeminiUsageStage, GeminiPricedModel> = {
  segmentation: 'gemini-3-flash-preview',
  pdfParser: 'gemini-3-flash-preview',
  dataExtraction: 'gemini-3.5-flash',
};

/**
 * Rough USD estimate for Gemini usage at one model's list price.
 * Uses token breakdown when present; does not include Search grounding or other fees.
 *
 * - Non-cached input: `promptTokenCount` minus `cachedContentTokenCount` (cached is a subset of prompt in typical API responses).
 * - Tool-use prompt tokens: added at input rate (if not already included in `promptTokenCount`, estimate may be slightly high).
 * - Output: `responseTokenCount` + `thoughtsTokenCount` at output rate (if both are populated and overlap, estimate may be high).
 */
export function estimateGeminiUsd(usage: UsageMetadata, model: GeminiPricedModel): number {
  const prompt = usage.promptTokenCount ?? 0;
  const cached = usage.cachedContentTokenCount ?? 0;
  const response = usage.responseTokenCount ?? 0;
  const toolUse = usage.toolUsePromptTokenCount ?? 0;
  const thoughts = usage.thoughtsTokenCount ?? 0;

  const { inputTextImageVideo, contextCaching, outputIncludingThinking } = GEMINI_STANDARD_USD_PER_MILLION[model];

  const nonCachedPrompt = Math.max(0, prompt - cached);
  const inputCost = (nonCachedPrompt + toolUse) * (inputTextImageVideo / 1_000_000);
  const cachedCost = cached * (contextCaching / 1_000_000);
  const outputCost = (response + thoughts) * (outputIncludingThinking / 1_000_000);

  return inputCost + cachedCost + outputCost;
}

/** {@link estimateGeminiUsd} at the stage's {@link GEMINI_STAGE_DEFAULT_MODEL}. */
export function estimateGeminiStageUsd(usage: UsageMetadata, stage: GeminiUsageStage): number {
  return estimateGeminiUsd(usage, GEMINI_STAGE_DEFAULT_MODEL[stage]);
}

const USAGE_FIELDS: (keyof UsageMetadata)[] = [
  'promptTokenCount',
  'cachedContentTokenCount',
  'responseTokenCount',
  'toolUsePromptTokenCount',
  'thoughtsTokenCount',
  'totalTokenCount',
];

/** Field-wise `total − part`, each field clamped at 0 so a bucket ahead of its total never prices negative. */
function subtractUsage(total: UsageMetadata, part: UsageMetadata): UsageMetadata {
  return Object.fromEntries(
    USAGE_FIELDS.map((key) => [key, Math.max(0, (total[key] ?? 0) - (part[key] ?? 0))]),
  ) as UsageMetadata;
}

/**
 * DR-4643: data-extraction USD. With the flash-preview bucket recorded, the bucket is priced at
 * `gemini-3-flash-preview` and the rest of the total at the stage default; without it, the whole
 * total is priced at the stage default.
 */
function estimateDataExtractionUsd(total: UsageMetadata, flashPreview: UsageMetadata | undefined): number {
  if (flashPreview === undefined) return estimateGeminiStageUsd(total, 'dataExtraction');
  return (
    estimateGeminiUsd(flashPreview, 'gemini-3-flash-preview') +
    estimateGeminiStageUsd(subtractUsage(total, flashPreview), 'dataExtraction')
  );
}

/** A model-usage snapshot as the document, project and organisation views hold it. */
export type ModelUsageCostInput = {
  segmentation?: UsageMetadata;
  pdfParser?: UsageMetadata;
  dataExtraction?: UsageMetadata;
  /**
   * DR-4643: the part of `dataExtraction` run on gemini-3-flash-preview categories (NIS
   * Statement, Part Movement Traceability Card). Absent when not recorded.
   */
  dataExtractionFlashPreview?: UsageMetadata;
  embeddings?: UsageMetadata;
  /** Recorded PDF→HTML conversion cost (USD); absent when none was recorded. */
  htmlConversionCostUsd?: number;
};

export type ModelUsageCostBreakdown = {
  segmentation: number;
  pdfParser: number;
  dataExtraction: number;
  embeddings: number;
  htmlConversion: number;
  total: number;
};

/**
 * Estimated USD per stage and in total for one usage snapshot. The single place the total is
 * computed, so the usage card and the organisation per-project table cannot drift apart.
 */
export function estimateModelUsageCost(input: ModelUsageCostInput): ModelUsageCostBreakdown {
  const segmentation = estimateGeminiStageUsd(input.segmentation ?? {}, 'segmentation');
  const pdfParser = estimateGeminiStageUsd(input.pdfParser ?? {}, 'pdfParser');
  const dataExtraction = estimateDataExtractionUsd(input.dataExtraction ?? {}, input.dataExtractionFlashPreview);
  const embeddings = estimateTextEmbedding3LargeUsd(input.embeddings ?? {});
  const htmlConversion = input.htmlConversionCostUsd ?? 0;
  return {
    segmentation,
    pdfParser,
    dataExtraction,
    embeddings,
    htmlConversion,
    total: segmentation + pdfParser + dataExtraction + embeddings + htmlConversion,
  };
}

export function formatUsdEstimate(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) {
    return '$0.000';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(amount);
}
